import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, GripVertical, Trash2, Settings2, Save, UploadCloud, Eye,
  LayoutGrid, ChevronDown, Layers, X, AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import StatusBadge from "@/components/common/StatusBadge";
import FormRenderer from "@/components/forms/FormRenderer";
import {
  FIELD_TYPES, CHOICE_TYPES, LAYOUTS, CONDITION_OPERATORS, VALIDATION_RULES, ROLE_CHOICES,
} from "@/lib/formEngine";
import { api, formatApiError } from "@/lib/api";

const uid = () => Math.random().toString(36).slice(2, 10);
const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

function newField(type = "text") {
  return {
    id: uid(), name: "", label: "New Field", type, help_text: "", placeholder: "", required: false,
    default_value: "", min_length: null, max_length: null, min_value: null, max_value: null,
    validation_message: "", active: true, order: 0, options: [], lookup_category: "",
    validations: [], file_types: [], max_file_mb: null,
    visible_roles: [], editable_roles: [], readonly_roles: [], hidden_roles: [], conditions: [],
  };
}
function newSection(order = 0) {
  return { id: uid(), title: "New Section", description: "", order, active: true, visible_roles: [], condition: null, fields: [] };
}

function RolePicker({ label, value = [], onChange, testId }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={testId} className="w-full flex items-center justify-between border border-[#E5E7EB] rounded-md px-3 py-2 text-sm text-left bg-white">
          <span className="text-[#475569] truncate">{value.length ? `${value.length} role(s)` : "All roles"}</span>
          <ChevronDown className="w-4 h-4 text-[#94A3B8]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2 max-h-72 overflow-y-auto">
        <div className="text-[11px] uppercase tracking-wide text-[#94A3B8] px-1 pb-1">{label}</div>
        {ROLE_CHOICES.map((r) => (
          <label key={r.code} className="flex items-center gap-2 px-1 py-1 text-sm cursor-pointer hover:bg-[#F5F7FA] rounded">
            <Checkbox checked={value.includes(r.code)} onCheckedChange={() => onChange(value.includes(r.code) ? value.filter((x) => x !== r.code) : [...value, r.code])} />
            {r.label}
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function OptionsEditor({ field, onChange }) {
  const opts = field.options || [];
  const set = (next) => onChange({ ...field, options: next });
  return (
    <div className="space-y-2" data-testid="options-editor">
      <Label className="text-sm font-medium text-[#1F2937]">Answer options</Label>
      {opts.map((o, i) => (
        <div key={o.id} className="flex items-center gap-2">
          <Input className="flex-1" placeholder="Label" value={o.label} onChange={(e) => set(opts.map((x) => x.id === o.id ? { ...x, label: e.target.value, value: x.value || slug(e.target.value) } : x))} data-testid={`option-label-${i}`} />
          <Input className="w-32 font-mono text-xs" placeholder="value" value={o.value} onChange={(e) => set(opts.map((x) => x.id === o.id ? { ...x, value: e.target.value } : x))} />
          <button className="text-[#EF4444] p-1.5 hover:bg-[#FEF2F2] rounded" onClick={() => set(opts.filter((x) => x.id !== o.id))} data-testid={`option-remove-${i}`}><X className="w-4 h-4" /></button>
        </div>
      ))}
      <Button variant="outline" size="sm" className="border-[#E5E7EB]" onClick={() => set([...opts, { id: uid(), label: `Option ${opts.length + 1}`, value: `option_${opts.length + 1}`, active: true, order: opts.length }])} data-testid="add-option"><Plus className="w-3.5 h-3.5 mr-1" /> Add option</Button>
    </div>
  );
}

function ConditionsEditor({ field, allFields, onChange }) {
  const conds = field.conditions || [];
  const set = (next) => onChange({ ...field, conditions: next });
  return (
    <div className="space-y-2" data-testid="conditions-editor">
      <Label className="text-sm font-medium text-[#1F2937]">Conditional logic</Label>
      {conds.map((c, i) => (
        <div key={i} className="border border-[#E5E7EB] rounded-md p-2 space-y-2 bg-[#FAFBFC]">
          <div className="flex items-center gap-2">
            <Select value={c.action} onValueChange={(v) => set(conds.map((x, j) => j === i ? { ...x, action: v } : x))}>
              <SelectTrigger className="w-28 h-8"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="show">Show this</SelectItem><SelectItem value="require">Require this</SelectItem></SelectContent>
            </Select>
            <span className="text-xs text-[#6B7280]">when</span>
            <button className="text-[#EF4444] p-1 ml-auto" onClick={() => set(conds.filter((_, j) => j !== i))} data-testid={`condition-remove-${i}`}><X className="w-4 h-4" /></button>
          </div>
          <div className="flex items-center gap-2">
            <Select value={c.field} onValueChange={(v) => set(conds.map((x, j) => j === i ? { ...x, field: v } : x))}>
              <SelectTrigger className="flex-1 h-8" data-testid={`condition-field-${i}`}><SelectValue placeholder="Field" /></SelectTrigger>
              <SelectContent>{allFields.filter((f) => f.name && f.id !== field.id).map((f) => <SelectItem key={f.id} value={f.name}>{f.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={c.operator} onValueChange={(v) => set(conds.map((x, j) => j === i ? { ...x, operator: v } : x))}>
              <SelectTrigger className="w-36 h-8"><SelectValue /></SelectTrigger>
              <SelectContent>{CONDITION_OPERATORS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {!["not_empty", "empty"].includes(c.operator) && (
            <Input className="h-8" placeholder="Value" value={c.value ?? ""} onChange={(e) => set(conds.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} data-testid={`condition-value-${i}`} />
          )}
        </div>
      ))}
      <Button variant="outline" size="sm" className="border-[#E5E7EB]" onClick={() => set([...conds, { field: "", operator: "equals", value: "", action: "show" }])} data-testid="add-condition"><Plus className="w-3.5 h-3.5 mr-1" /> Add rule</Button>
    </div>
  );
}

function FieldEditor({ field, allFields, categories, onChange, onClose }) {
  const upd = (patch) => onChange({ ...field, ...patch });
  const isChoice = CHOICE_TYPES.includes(field.type);
  return (
    <div className="space-y-4" data-testid="field-editor">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="text-sm font-medium">Label</Label>
          <Input value={field.label} onChange={(e) => upd({ label: e.target.value, name: field.name || slug(e.target.value) })} data-testid="fe-label" />
        </div>
        <div>
          <Label className="text-sm font-medium">Internal name</Label>
          <Input className="font-mono text-xs" value={field.name} onChange={(e) => upd({ name: slug(e.target.value) })} data-testid="fe-name" />
        </div>
        <div>
          <Label className="text-sm font-medium">Field type</Label>
          <Select value={field.type} onValueChange={(v) => upd({ type: v })}>
            <SelectTrigger data-testid="fe-type"><SelectValue /></SelectTrigger>
            <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t.type} value={t.type}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label className="text-sm font-medium">Help text</Label>
          <Input value={field.help_text || ""} onChange={(e) => upd({ help_text: e.target.value })} />
        </div>
        <div className="col-span-2">
          <Label className="text-sm font-medium">Placeholder</Label>
          <Input value={field.placeholder || ""} onChange={(e) => upd({ placeholder: e.target.value })} />
        </div>
      </div>

      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm"><Switch checked={field.required} onCheckedChange={(v) => upd({ required: v })} data-testid="fe-required" /> Required</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={field.active} onCheckedChange={(v) => upd({ active: v })} data-testid="fe-active" /> Active</label>
      </div>

      {isChoice && (
        <div className="border-t border-[#E5E7EB] pt-3 space-y-3">
          <div>
            <Label className="text-sm font-medium">Bind to Lookup (optional)</Label>
            <Select value={field.lookup_category || "none"} onValueChange={(v) => upd({ lookup_category: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="fe-lookup"><SelectValue placeholder="Use static options" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Static options (below)</SelectItem>
                {categories.map((c) => <SelectItem key={c.code} value={c.code}>Lookup: {c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[#94A3B8] mt-1">Lookup-bound fields auto-reflect Lookups Management changes.</p>
          </div>
          {!field.lookup_category && <OptionsEditor field={field} onChange={onChange} />}
        </div>
      )}

      {["text", "textarea", "email", "phone"].includes(field.type) && (
        <div className="grid grid-cols-2 gap-3 border-t border-[#E5E7EB] pt-3">
          <div><Label className="text-sm font-medium">Min length</Label><Input type="number" value={field.min_length ?? ""} onChange={(e) => upd({ min_length: e.target.value ? Number(e.target.value) : null })} /></div>
          <div><Label className="text-sm font-medium">Max length</Label><Input type="number" value={field.max_length ?? ""} onChange={(e) => upd({ max_length: e.target.value ? Number(e.target.value) : null })} /></div>
        </div>
      )}
      {field.type === "number" && (
        <div className="grid grid-cols-2 gap-3 border-t border-[#E5E7EB] pt-3">
          <div><Label className="text-sm font-medium">Min value</Label><Input type="number" value={field.min_value ?? ""} onChange={(e) => upd({ min_value: e.target.value ? Number(e.target.value) : null })} /></div>
          <div><Label className="text-sm font-medium">Max value</Label><Input type="number" value={field.max_value ?? ""} onChange={(e) => upd({ max_value: e.target.value ? Number(e.target.value) : null })} /></div>
        </div>
      )}

      <div className="border-t border-[#E5E7EB] pt-3">
        <Label className="text-sm font-medium">Validations</Label>
        <div className="flex flex-wrap gap-2 mt-1.5">
          {VALIDATION_RULES.map((r) => {
            const on = (field.validations || []).includes(r.value);
            return (
              <button key={r.value} onClick={() => upd({ validations: on ? field.validations.filter((x) => x !== r.value) : [...(field.validations || []), r.value] })}
                className={`px-2.5 py-1 rounded-full text-xs border ${on ? "bg-[#0B7E8C] text-white border-[#0B7E8C]" : "border-[#E5E7EB] text-[#475569]"}`} data-testid={`validation-${r.value}`}>{r.label}</button>
            );
          })}
        </div>
        <Input className="mt-2" placeholder="Custom validation message (optional)" value={field.validation_message || ""} onChange={(e) => upd({ validation_message: e.target.value })} />
      </div>

      <div className="border-t border-[#E5E7EB] pt-3">
        <ConditionsEditor field={field} allFields={allFields} onChange={onChange} />
      </div>

      <div className="border-t border-[#E5E7EB] pt-3 grid grid-cols-2 gap-3">
        <div><Label className="text-sm font-medium">Visible roles</Label><RolePicker label="Visible to" value={field.visible_roles} onChange={(v) => upd({ visible_roles: v })} testId="fe-visible-roles" /></div>
        <div><Label className="text-sm font-medium">Editable roles</Label><RolePicker label="Editable by" value={field.editable_roles} onChange={(v) => upd({ editable_roles: v })} testId="fe-editable-roles" /></div>
        <div><Label className="text-sm font-medium">Read-only roles</Label><RolePicker label="Read-only for" value={field.readonly_roles} onChange={(v) => upd({ readonly_roles: v })} testId="fe-readonly-roles" /></div>
        <div><Label className="text-sm font-medium">Hidden roles</Label><RolePicker label="Hidden from" value={field.hidden_roles} onChange={(v) => upd({ hidden_roles: v })} testId="fe-hidden-roles" /></div>
      </div>
    </div>
  );
}

export default function FormDesigner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [definition, setDefinition] = useState({ layout: "single", sections: [] });
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(null); // {sectionId, fieldId} | {sectionId} for section edit
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRole, setPreviewRole] = useState("admin");
  const [previewValues, setPreviewValues] = useState({});

  const canWrite = form?.can_write;

  useEffect(() => {
    api.get("/forms/categories").then((r) => setCategories(r.data.items)).catch(() => {});
    api.get(`/forms/${id}`).then((r) => {
      setForm(r.data);
      setDefinition(r.data.working_version?.definition || { layout: "single", sections: [] });
    }).catch((e) => { toast.error(formatApiError(e)); navigate("/forms"); });
    // eslint-disable-next-line
  }, [id]);

  const allFields = useMemo(() => (definition.sections || []).flatMap((s) => s.fields || []), [definition]);

  const update = (next) => { setDefinition(next); setDirty(true); };

  const updateSection = (sid, patch) => update({ ...definition, sections: definition.sections.map((s) => s.id === sid ? { ...s, ...patch } : s) });
  const updateField = (sid, fid, nextField) => update({
    ...definition,
    sections: definition.sections.map((s) => s.id === sid ? { ...s, fields: s.fields.map((f) => f.id === fid ? nextField : f) } : s),
  });
  const addSection = () => update({ ...definition, sections: [...definition.sections, newSection(definition.sections.length)] });
  const removeSection = (sid) => { update({ ...definition, sections: definition.sections.filter((s) => s.id !== sid) }); setSelected(null); };
  const addField = (sid, type = "text") => {
    const f = newField(type);
    f.name = `field_${allFields.length + 1}`;
    update({ ...definition, sections: definition.sections.map((s) => s.id === sid ? { ...s, fields: [...s.fields, f] } : s) });
    setSelected({ sectionId: sid, fieldId: f.id });
  };
  const removeField = (sid, fid) => { update({ ...definition, sections: definition.sections.map((s) => s.id === sid ? { ...s, fields: s.fields.filter((f) => f.id !== fid) } : s) }); setSelected(null); };

  function onDragEnd(result) {
    const { source, destination, type } = result;
    if (!destination) return;
    if (type === "section") {
      const secs = [...definition.sections];
      const [moved] = secs.splice(source.index, 1);
      secs.splice(destination.index, 0, moved);
      update({ ...definition, sections: secs.map((s, i) => ({ ...s, order: i })) });
      return;
    }
    // field drag (droppableId = section id)
    const srcSec = definition.sections.find((s) => s.id === source.droppableId);
    const dstSec = definition.sections.find((s) => s.id === destination.droppableId);
    if (!srcSec || !dstSec) return;
    const srcFields = [...srcSec.fields];
    const [moved] = srcFields.splice(source.index, 1);
    if (srcSec.id === dstSec.id) {
      srcFields.splice(destination.index, 0, moved);
      update({ ...definition, sections: definition.sections.map((s) => s.id === srcSec.id ? { ...s, fields: srcFields.map((f, i) => ({ ...f, order: i })) } : s) });
    } else {
      const dstFields = [...dstSec.fields];
      dstFields.splice(destination.index, 0, moved);
      update({
        ...definition,
        sections: definition.sections.map((s) => {
          if (s.id === srcSec.id) return { ...s, fields: srcFields.map((f, i) => ({ ...f, order: i })) };
          if (s.id === dstSec.id) return { ...s, fields: dstFields.map((f, i) => ({ ...f, order: i })) };
          return s;
        }),
      });
    }
  }

  async function save() {
    setSaving(true);
    try {
      const r = await api.put(`/forms/${id}`, { definition });
      setForm((p) => ({ ...p, ...r.data }));
      setDirty(false);
      toast.success(r.data.version_message || "Saved");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  }
  async function publish() {
    if (dirty) await save();
    try { const r = await api.post(`/forms/${id}/publish`); setForm((p) => ({ ...p, ...r.data })); toast.success("Form published"); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  if (!form) return <div className="py-20 text-center text-[#6B7280]" data-testid="designer-loading">Loading…</div>;

  const selField = selected?.fieldId ? definition.sections.find((s) => s.id === selected.sectionId)?.fields.find((f) => f.id === selected.fieldId) : null;
  const selSection = selected && !selected.fieldId ? definition.sections.find((s) => s.id === selected.sectionId) : null;

  return (
    <div data-testid="form-designer-page">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate("/forms")} data-testid="designer-back"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-[#1F2937] truncate">{form.name}</h1>
              <StatusBadge status={form.status} />
            </div>
            <div className="text-xs text-[#94A3B8] font-mono flex items-center gap-2">
              <span>{form.key} · v{form.latest_version}{form.latest_version > (form.published_version || 0) && " (draft)"}</span>
              {(dirty || form.latest_version > (form.published_version || 0)) && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-[#FEF3C7] text-[#92400E] font-sans" data-testid="designer-unpublished-pill">
                  ● Unpublished changes — not live yet
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 mr-1">
            <LayoutGrid className="w-4 h-4 text-[#94A3B8]" />
            <Select value={definition.layout} onValueChange={(v) => update({ ...definition, layout: v })} disabled={!canWrite}>
              <SelectTrigger className="w-[180px] h-9" data-testid="layout-select"><SelectValue /></SelectTrigger>
              <SelectContent>{LAYOUTS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button variant="outline" className="border-[#E5E7EB]" onClick={() => setPreviewOpen(true)} data-testid="designer-preview"><Eye className="w-4 h-4 mr-1.5" /> Preview</Button>
          {canWrite && <Button variant="outline" className="border-[#E5E7EB]" onClick={save} disabled={saving || !dirty} data-testid="designer-save"><Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : dirty ? "Save" : "Saved"}</Button>}
          {canWrite && (() => {
            const hasUnpublished = dirty || form.latest_version > (form.published_version || 0);
            return (
              <Button
                onClick={publish}
                disabled={!hasUnpublished}
                data-testid="designer-publish"
                className={hasUnpublished ? "bg-[#0B7E8C] hover:bg-[#128C97] text-white" : "bg-[#E5E7EB] text-[#9CA3AF] hover:bg-[#E5E7EB] cursor-default"}
              >
                <UploadCloud className="w-4 h-4 mr-1.5" /> {hasUnpublished ? "Publish changes" : "Published"}
              </Button>
            );
          })()}
        </div>
      </div>

      {!canWrite && (
        <div className="mb-4 flex items-center gap-2 bg-[#FEF9C3] border border-[#FDE68A] text-[#854D0E] text-sm rounded-lg px-4 py-2.5" data-testid="readonly-banner">
          <AlertCircle className="w-4 h-4" /> You have read-only access to this form's category.
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        {/* Structure */}
        <div className="col-span-12 lg:col-span-7">
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="sections" type="section">
              {(prov) => (
                <div ref={prov.innerRef} {...prov.droppableProps} className="space-y-3">
                  {definition.sections.map((sec, si) => (
                    <Draggable key={sec.id} draggableId={sec.id} index={si} isDragDisabled={!canWrite}>
                      {(p) => (
                        <div ref={p.innerRef} {...p.draggableProps} className="hah-card p-4" data-testid={`section-${si}`}>
                          <div className="flex items-center gap-2 mb-3">
                            <span {...p.dragHandleProps} className="cursor-grab text-[#CBD5E1]"><GripVertical className="w-4 h-4" /></span>
                            <Layers className="w-4 h-4 text-[#0B7E8C]" />
                            <Input value={sec.title} disabled={!canWrite} onChange={(e) => updateSection(sec.id, { title: e.target.value })} className="font-semibold border-0 px-1 focus-visible:ring-0 flex-1" data-testid={`section-title-${si}`} />
                            <button className="p-1.5 hover:bg-[#F5F7FA] rounded" onClick={() => setSelected({ sectionId: sec.id })} data-testid={`section-settings-${si}`} title="Section settings"><Settings2 className="w-4 h-4 text-[#6B7280]" /></button>
                            {canWrite && <button className="p-1.5 hover:bg-[#FEF2F2] rounded text-[#EF4444]" onClick={() => removeSection(sec.id)} data-testid={`section-remove-${si}`}><Trash2 className="w-4 h-4" /></button>}
                          </div>
                          <Droppable droppableId={sec.id} type="field">
                            {(fprov) => (
                              <div ref={fprov.innerRef} {...fprov.droppableProps} className="space-y-1.5 min-h-[10px]">
                                {sec.fields.map((f, fi) => (
                                  <Draggable key={f.id} draggableId={f.id} index={fi} isDragDisabled={!canWrite}>
                                    {(fp) => (
                                      <div ref={fp.innerRef} {...fp.draggableProps}
                                        onClick={() => setSelected({ sectionId: sec.id, fieldId: f.id })}
                                        className={`flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer ${selected?.fieldId === f.id ? "border-[#0B7E8C] bg-[#EAF8FA]" : "border-[#E5E7EB] hover:bg-[#F8FAFC]"} ${!f.active ? "opacity-50" : ""}`}
                                        data-testid={`field-item-${f.name || f.id}`}>
                                        <span {...fp.dragHandleProps} className="cursor-grab text-[#CBD5E1]" onClick={(e) => e.stopPropagation()}><GripVertical className="w-4 h-4" /></span>
                                        <div className="flex-1 min-w-0">
                                          <div className="text-sm font-medium text-[#1F2937] truncate">{f.label}{f.required && <span className="text-[#EF4444] ml-1">*</span>}</div>
                                          <div className="text-[11px] text-[#94A3B8] font-mono">{f.name || "(unnamed)"} · {f.type}</div>
                                        </div>
                                        {f.lookup_category && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EDE9FE] text-[#6D28D9]">lookup</span>}
                                        {(f.conditions || []).length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#DBEAFE] text-[#1D4ED8]">logic</span>}
                                        {canWrite && <button className="p-1 text-[#EF4444]" onClick={(e) => { e.stopPropagation(); removeField(sec.id, f.id); }} data-testid={`field-remove-${f.name || f.id}`}><X className="w-4 h-4" /></button>}
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {fprov.placeholder}
                                {sec.fields.length === 0 && <div className="text-xs text-[#94A3B8] italic py-2 text-center">No fields yet.</div>}
                              </div>
                            )}
                          </Droppable>
                          {canWrite && (
                            <div className="mt-2 flex items-center gap-2">
                              <Select onValueChange={(t) => addField(sec.id, t)}>
                                <SelectTrigger className="h-8 w-[170px]" data-testid={`add-field-${si}`}><span className="flex items-center text-sm text-[#0B7E8C]"><Plus className="w-3.5 h-3.5 mr-1" /> Add field</span></SelectTrigger>
                                <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t.type} value={t.type}>{t.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {prov.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          {canWrite && <Button variant="outline" className="mt-3 border-dashed border-[#CBD5E1] w-full" onClick={addSection} data-testid="add-section"><Plus className="w-4 h-4 mr-1.5" /> Add Section</Button>}
          {definition.sections.length === 0 && <div className="text-sm text-[#94A3B8] text-center py-8">Start by adding a section.</div>}
        </div>

        {/* Editor panel */}
        <div className="col-span-12 lg:col-span-5">
          <div className="hah-card p-5 sticky top-4" data-testid="editor-panel">
            {selField ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-[#1F2937] flex items-center gap-2"><Settings2 className="w-4 h-4 text-[#0B7E8C]" /> Field Settings</h3>
                </div>
                <fieldset disabled={!canWrite} className={!canWrite ? "opacity-70" : ""}>
                  <FieldEditor field={selField} allFields={allFields} categories={categories} onChange={(nf) => updateField(selected.sectionId, selected.fieldId, nf)} onClose={() => setSelected(null)} />
                </fieldset>
              </>
            ) : selSection ? (
              <>
                <h3 className="text-sm font-semibold text-[#1F2937] flex items-center gap-2 mb-4"><Layers className="w-4 h-4 text-[#0B7E8C]" /> Section Settings</h3>
                <fieldset disabled={!canWrite} className="space-y-4">
                  <div><Label className="text-sm font-medium">Title</Label><Input value={selSection.title} onChange={(e) => updateSection(selSection.id, { title: e.target.value })} data-testid="se-title" /></div>
                  <div><Label className="text-sm font-medium">Description</Label><Textarea value={selSection.description || ""} onChange={(e) => updateSection(selSection.id, { description: e.target.value })} /></div>
                  <label className="flex items-center gap-2 text-sm"><Switch checked={selSection.active} onCheckedChange={(v) => updateSection(selSection.id, { active: v })} /> Active</label>
                  <div><Label className="text-sm font-medium">Visible roles</Label><RolePicker label="Visible to" value={selSection.visible_roles} onChange={(v) => updateSection(selSection.id, { visible_roles: v })} testId="se-visible-roles" /></div>
                </fieldset>
              </>
            ) : (
              <div className="text-center text-sm text-[#94A3B8] py-12" data-testid="editor-empty">
                <Settings2 className="w-8 h-8 mx-auto mb-2 text-[#CBD5E1]" />
                Select a field or section to configure it.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview */}
      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent side="right" className="w-full sm:max-w-[720px] overflow-y-auto p-0 bg-[#FAFBFC]">
          <SheetHeader className="px-6 py-5 border-b border-[#E5E7EB] bg-white">
            <SheetTitle>Preview · {form.name}</SheetTitle>
          </SheetHeader>
          <div className="px-6 py-3 border-b border-[#E5E7EB] bg-white flex items-center gap-2">
            <span className="text-xs text-[#6B7280]">As role:</span>
            <Select value={previewRole} onValueChange={setPreviewRole}>
              <SelectTrigger className="w-[200px] h-9" data-testid="designer-preview-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLE_CHOICES.map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="p-6"><FormRenderer definition={definition} role={previewRole} values={previewValues} onChange={setPreviewValues} /></div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
